import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget for the sign-in form (SECURITY_&_UAC §3.1).
 * Active only when the build sets VITE_TURNSTILE_SITE_KEY (deployed
 * staging/production); local development builds render nothing and the API
 * skips verification while its TURNSTILE_SECRET is unset. Tokens are
 * single-use — the parent remounts the widget (key bump) after a failed
 * submit so the retry gets a fresh token.
 */

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ?? ''

export const turnstileEnabled = SITE_KEY.length > 0

interface TurnstileApi {
  render: (
    container: HTMLElement,
    params: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback': () => void
      'error-callback': () => void
      theme: 'auto'
    },
  ) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    onFloorfileTurnstileLoad?: () => void
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onFloorfileTurnstileLoad&render=explicit'

let scriptLoading: Promise<TurnstileApi> | undefined

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  scriptLoading ??= new Promise<TurnstileApi>((resolve, reject) => {
    window.onFloorfileTurnstileLoad = () => {
      if (window.turnstile) resolve(window.turnstile)
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onerror = () => {
      scriptLoading = undefined
      reject(new Error('Turnstile failed to load'))
    }
    document.head.appendChild(script)
  })
  return scriptLoading
}

export function TurnstileWidget({ onToken }: { onToken: (token: string | undefined) => void }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  useEffect(() => {
    if (!turnstileEnabled) return undefined
    let widgetId: string | undefined
    let cancelled = false
    void loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return
        widgetId = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(undefined),
          'error-callback': () => onTokenRef.current(undefined),
          theme: 'auto',
        })
      })
      .catch(() => onTokenRef.current(undefined))
    return () => {
      cancelled = true
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [])

  if (!turnstileEnabled) return null
  return <div ref={containerRef} className="flex justify-center" />
}
